import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { api, ApiError } from '../api/client'
import { SupportInboxFilters } from '../components/SupportInboxFilters'
import { PrintSettingsPanel } from '../components/PrintSettingsPanel'

/**
 * Mostly read-only view of which external credentials this deployment has.
 *
 * Secret values never reach the browser — the API returns presence
 * booleans for anything auth-bearing (see aamcirc/admin/routers/settings.py).
 * The one exception is the Stripe group's membership-plans table: prices
 * and active flags are stored in our own database (not .env), so they are
 * editable here via `GET`/`PUT /api/admin/settings/plans`.
 */

interface SettingEntry {
  name: string
  configured: boolean
  value: string | null
  secret: boolean
}

interface SettingGroup {
  key: string
  label: string
  mode: string | null
  ready: boolean
  entries: SettingEntry[]
  incomplete_publications: string[]
  note: string | null
}

const PUB_NAMES: Record<string, string> = {
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  rc_car_action: 'RC Car Action',
  diecast: 'Die Cast X',
}

/** Live/production modes are the ones worth double-taking at. */
const LIVE_MODES = new Set(['live', 'production'])

function ModeBadge({ mode }: { mode: string | null }) {
  if (!mode) return null
  const live = LIVE_MODES.has(mode.toLowerCase())
  return (
    <span
      className={`font-mono text-[10px] tracking-label uppercase border px-1.5 py-0.5 ${
        live
          ? 'border-radar-green text-radar-green'
          : 'border-amber-glow text-amber-glow'
      }`}
    >
      {mode}
    </span>
  )
}

function ReadyBadge({ ready }: { ready: boolean }) {
  return (
    <span
      className={`font-mono text-[10px] tracking-label uppercase border px-1.5 py-0.5 ${
        ready
          ? 'border-radar-green text-radar-green'
          : 'border-radar-red text-radar-red'
      }`}
    >
      {ready ? 'configured' : 'incomplete'}
    </span>
  )
}

function EntryRow({ entry }: { entry: SettingEntry }) {
  return (
    <tr className="border-t border-ink-500/60">
      <td className="py-1.5 pr-4 font-mono text-[11px] text-paper-dim align-top">
        {entry.name}
      </td>
      <td className="py-1.5 pr-4 align-top">
        <span
          className={`font-mono text-[10px] tracking-label uppercase ${
            entry.configured ? 'text-radar-green' : 'text-paper-trace'
          }`}
        >
          {entry.configured ? 'set' : 'not set'}
        </span>
      </td>
      <td className="py-1.5 font-mono text-[11px] text-paper break-all align-top">
        {entry.secret ? (
          <span className="text-paper-trace" title="Secret values are never sent to the browser">
            —
          </span>
        ) : (
          entry.value ?? <span className="text-paper-trace">—</span>
        )}
      </td>
    </tr>
  )
}

interface MembershipPlan {
  publication_slug: string
  plan_id: string
  amount_cents: number
  currency: string
  term_months: number
  active: boolean
}

function centsToDollarsStr(cents: number): string {
  return (cents / 100).toFixed(2)
}

function PlanRow({ plan }: { plan: MembershipPlan }) {
  const queryClient = useQueryClient()
  const [dollars, setDollars] = useState(centsToDollarsStr(plan.amount_cents))
  const [active, setActive] = useState(plan.active)

  const parsed = parseFloat(dollars)
  const invalid = Number.isNaN(parsed) || parsed <= 0

  const mutation = useMutation({
    mutationFn: () =>
      api.put<MembershipPlan>('/api/admin/settings/plans', {
        publication_slug: plan.publication_slug,
        plan_id: plan.plan_id,
        amount_cents: Math.round(parsed * 100),
        active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'plans'] })
    },
  })

  const dirty =
    dollars !== centsToDollarsStr(plan.amount_cents) || active !== plan.active

  return (
    <tr className="border-t border-ink-500/60">
      <td className="py-1.5 pr-4 font-mono text-[11px] text-paper align-top">
        {PUB_NAMES[plan.publication_slug] ?? plan.publication_slug}
      </td>
      <td className="py-1.5 pr-4 font-mono text-[11px] text-paper-dim align-top">
        {plan.plan_id}
      </td>
      <td className="py-1.5 pr-4 align-top">
        <span className="font-mono text-[11px] text-paper-dim mr-1">$</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={dollars}
          onChange={(e) => setDollars(e.target.value)}
          className="w-20 bg-ink-900 border border-ink-500 px-1.5 py-0.5 font-mono text-[11px] text-paper"
        />
      </td>
      <td className="py-1.5 pr-4 align-top">
        <label className="inline-flex items-center gap-1.5 font-mono text-[11px] text-paper-dim">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          active
        </label>
      </td>
      <td className="py-1.5 align-top">
        <Button
          size="sm"
          variant="outline"
          disabled={invalid || !dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {invalid && (
          <span className="ml-2 font-mono text-[10px] text-radar-red">
            Enter a price above $0.00
          </span>
        )}
        {!invalid && mutation.isError && (
          <span className="ml-2 font-mono text-[10px] text-radar-red">
            {mutation.error instanceof Error ? mutation.error.message : 'save failed'}
          </span>
        )}
        {!invalid && mutation.isSuccess && !dirty && (
          <span className="ml-2 font-mono text-[10px] text-radar-green">saved</span>
        )}
      </td>
    </tr>
  )
}

function MembershipPlansTable() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', 'plans'],
    queryFn: () => api.get<{ plans: MembershipPlan[] }>('/api/admin/settings/plans'),
  })

  return (
    <div className="mt-4 pt-4 border-t border-ink-500/60">
      <p className="label mb-2">Membership plans</p>
      {isLoading && <p className="label">Loading…</p>}
      {error instanceof Error && <p className="text-red-400">{error.message}</p>}
      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="label pb-1 pr-4">Publication</th>
                <th className="label pb-1 pr-4">Plan</th>
                <th className="label pb-1 pr-4">Amount</th>
                <th className="label pb-1 pr-4">Active</th>
                <th className="label pb-1"></th>
              </tr>
            </thead>
            <tbody>
              {data.plans.map((p) => (
                <PlanRow key={`${p.publication_slug}:${p.plan_id}`} plan={p} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="label mt-2">
        A price change affects new signups only — every existing member's
        renewal stays locked at what they signed up for.
      </p>
    </div>
  )
}

interface CadenceVersion {
  id: number
  offset_days: number[]
  note: string | null
  created_at: string | null
  created_by: string | null
}

type CadencePlan = 'annual' | 'monthly'

interface RenewalCadence {
  plan: CadencePlan
  offset_days: number[]
  is_default: boolean
  attempts: number
  span_days: number
  access_through_day: number
  max_attempts: number
  max_offset_days: number
  versions: CadenceVersion[]
}

/** The API's sentence when it refuses a cadence, not a generic HTTP one. */
function apiDetail(error: unknown): string {
  if (error instanceof ApiError) {
    const d = error.detail
    if (d && typeof d === 'object' && 'detail' in d) return String((d as { detail: unknown }).detail)
  }
  return error instanceof Error ? error.message : 'save failed'
}

/** Mirrors aamcirc.payments.cadence.validate, so a bad list is caught
 *  before the round trip. The server still decides. */
function cadenceProblem(days: number[], limits: RenewalCadence): string | null {
  if (days.length === 0) return 'A cadence needs at least one attempt day.'
  if (days.length > limits.max_attempts) return `At most ${limits.max_attempts} attempts.`
  if (days.some((d) => !Number.isInteger(d) || d < 0)) return 'Attempt days must be whole days.'
  if (days[0] !== 0) return 'The first attempt is always day 0.'
  if (days.some((d, i) => i > 0 && d <= days[i - 1])) return 'Attempt days must increase.'
  if (days[days.length - 1] > limits.max_offset_days)
    return `The last attempt cannot be more than ${limits.max_offset_days} days out.`
  return null
}

function formatDay(d: string | null): string {
  if (!d) return ''
  return new Date(d).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

function CadenceEditor({ cadence }: { cadence: RenewalCadence }) {
  const queryClient = useQueryClient()
  const [days, setDays] = useState<number[]>(cadence.offset_days)
  const [note, setNote] = useState('')

  const problem = cadenceProblem(days, cadence)
  const dirty = days.join(',') !== cadence.offset_days.join(',')

  const mutation = useMutation({
    mutationFn: () =>
      api.post<RenewalCadence>(`/api/admin/settings/renewal-cadence?plan=${cadence.plan}`, {
        offset_days: days,
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: () => {
      setNote('')
      queryClient.invalidateQueries({ queryKey: ['settings', 'renewal-cadence', cadence.plan] })
    },
  })

  const setDay = (i: number, raw: string) => {
    const n = parseInt(raw, 10)
    setDays(days.map((d, j) => (j === i ? (Number.isNaN(n) ? 0 : n) : d)))
  }

  const addDay = () => {
    const last = days[days.length - 1] ?? 0
    setDays([...days, Math.min(last + 3, cadence.max_offset_days)])
  }

  const planLabel = cadence.plan === 'monthly' ? 'Monthly' : 'Annual'

  return (
    <div className="mt-4 pt-4 border-t border-ink-500/60">
      <p className="label mb-2">Renewal cadence · {planLabel} memberships</p>
      <p className="label mb-3">
        Days after the first renewal attempt on which we try the card again
        (the first attempt is the day the paid period ends, or the day billing
        starts if later). A hard decline (lost, stolen, closed or expired card)
        pauses the retries until the card changes. A card the member adds is
        charged within 15 minutes; one Authorize.Net&rsquo;s Account Updater
        replaces is picked up by the next morning&rsquo;s check. Retries are always
        at least two days apart, and nothing is charged after the last day.
        A change applies to periods ending from here on — a member already
        part-way through their retries finishes on the days they started with,
        which are the ones we emailed them.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        {days.map((d, i) => (
          <div key={i} className="flex flex-col gap-1">
            <span className="label">
              {i === 0 ? 'Due date' : `Retry ${i}`}
            </span>
            <div className="flex items-center gap-1">
              <span className="font-mono text-[11px] text-paper-dim">day</span>
              <input
                type="number"
                min="0"
                max={cadence.max_offset_days}
                value={d}
                disabled={i === 0}
                onChange={(e) => setDay(i, e.target.value)}
                className="w-14 bg-ink-900 border border-ink-500 px-1.5 py-0.5 font-mono text-[11px] text-paper disabled:text-paper-trace"
              />
              {i > 0 && i === days.length - 1 && (
                <button
                  type="button"
                  aria-label={`Remove retry ${i}`}
                  onClick={() => setDays(days.slice(0, -1))}
                  className="font-mono text-[11px] text-paper-trace hover:text-radar-red px-1"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        ))}
        {days.length < cadence.max_attempts && (
          <Button size="sm" variant="outline" onClick={addDay}>
            + attempt
          </Button>
        )}
      </div>

      <p className="mt-3 font-mono text-[11px] text-paper-dim">
        {days.length} attempt{days.length === 1 ? '' : 's'} over{' '}
        {days[days.length - 1] ?? 0} day{(days[days.length - 1] ?? 0) === 1 ? '' : 's'} ·
        members keep reading until the last attempt fails (day{' '}
        {Math.max(days[days.length - 1] ?? 0, 7)}) ·
        the last failure expires the membership
      </p>

      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={note}
          maxLength={200}
          placeholder="Why the change (optional)"
          onChange={(e) => setNote(e.target.value)}
          className="w-72 bg-ink-900 border border-ink-500 px-1.5 py-0.5 font-mono text-[11px] text-paper"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={problem !== null || !dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : 'Save cadence'}
        </Button>
        {problem && <span className="font-mono text-[10px] text-radar-red">{problem}</span>}
        {!problem && mutation.isError && (
          <span className="font-mono text-[10px] text-radar-red">{apiDetail(mutation.error)}</span>
        )}
        {!problem && mutation.isSuccess && !dirty && (
          <span className="font-mono text-[10px] text-radar-green">saved</span>
        )}
      </div>

      {cadence.is_default && (
        <p className="label mt-2">
          Never changed — these are the days the engine ships with.
        </p>
      )}

      {cadence.versions.length > 0 && (
        <table className="mt-3 text-left">
          <thead>
            <tr>
              <th className="label pb-1 pr-4">Saved</th>
              <th className="label pb-1 pr-4">Days</th>
              <th className="label pb-1 pr-4">By</th>
              <th className="label pb-1">Note</th>
            </tr>
          </thead>
          <tbody>
            {cadence.versions.map((v, i) => (
              <tr key={v.id} className="border-t border-ink-500/60">
                <td className="py-1.5 pr-4 font-mono text-[11px] text-paper-dim">
                  {formatDay(v.created_at)}
                  {i === 0 && <span className="ml-2 text-radar-green">active</span>}
                </td>
                <td className="py-1.5 pr-4 font-mono text-[11px] text-paper">
                  {v.offset_days.join(' · ')}
                </td>
                <td className="py-1.5 pr-4 font-mono text-[11px] text-paper-dim">
                  {v.created_by ?? '—'}
                </td>
                <td className="py-1.5 font-mono text-[11px] text-paper-dim">{v.note ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function RenewalCadencePanel() {
  return (
    <>
      <RenewalCadenceForPlan plan="annual" />
      <RenewalCadenceForPlan plan="monthly" />
    </>
  )
}

function RenewalCadenceForPlan({ plan }: { plan: CadencePlan }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', 'renewal-cadence', plan],
    queryFn: () => api.get<RenewalCadence>(`/api/admin/settings/renewal-cadence?plan=${plan}`),
  })

  if (isLoading) return <p className="label mt-4">Loading…</p>
  if (error instanceof Error) return <p className="text-red-400 mt-4">{error.message}</p>
  if (!data) return null
  // Remounts on every save so the editor starts from what was stored.
  return <CadenceEditor key={`${plan}-${data.versions[0]?.id ?? 'default'}`} cadence={data} />
}

export function SettingsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings', 'integrations'],
    queryFn: () =>
      api.get<{ groups: SettingGroup[] }>('/api/admin/settings/integrations'),
    staleTime: 60_000,
  })

  return (
    <div className="px-8 py-6 space-y-6">
      <header>
        <h1 className="font-serif text-3xl text-paper">Settings</h1>
        <p className="label mt-1">
          Which external credentials this deployment has, and in which mode.
          Credentials are read-only — they live in{' '}
          <span className="font-mono">.env</span> and are read once at boot,
          so changing one needs an API restart. Membership plan prices and the
          renewal cadence are editable here, below the Payments group.
        </p>
      </header>

      {isLoading && <p className="label">Loading…</p>}
      {error instanceof Error && <p className="text-red-400">{error.message}</p>}

      {(data?.groups ?? []).map((g) => (
        <Panel
          key={g.key}
          label={g.label}
          accent={g.ready ? 'green' : 'red'}
          right={
            <>
              <ModeBadge mode={g.mode} />
              <ReadyBadge ready={g.ready} />
            </>
          }
        >
          {g.note && <p className="label mb-3">{g.note}</p>}

          {g.incomplete_publications.length > 0 && (
            <p className="mb-3 font-mono text-[11px] text-radar-red">
              ⚠ Not wired up for:{' '}
              {g.incomplete_publications
                .map((p) => PUB_NAMES[p] ?? p)
                .join(', ')}
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr>
                  <th className="label pb-1 pr-4">Setting</th>
                  <th className="label pb-1 pr-4">Status</th>
                  <th className="label pb-1">Value</th>
                </tr>
              </thead>
              <tbody>
                {g.entries.map((e) => (
                  <EntryRow key={e.name} entry={e} />
                ))}
              </tbody>
            </table>
          </div>

          {g.key === 'payments' && <MembershipPlansTable />}
          {g.key === 'payments' && <RenewalCadencePanel />}
          {g.key === 'payments' && <PrintSettingsPanel />}
          {g.key === 'payments' && (
            <p className="label mt-2 text-paper-trace">
              These are also on the Print screen, with the order queue.
            </p>
          )}
        </Panel>
      ))}

      <div id="support-inbox">
        <Panel label="Support inbox">
          <SupportInboxFilters />
        </Panel>
      </div>

      <p className="label">
        Secrets show as “set” or “not set” only — no value, masked or
        otherwise, is sent to the browser.
      </p>
    </div>
  )
}
